import React, { memo } from 'react';

/**
 * Shared SVG playing card art — used by Durak and Solitaire so both games deal the same deck.
 * Classic suits (♠ ♥ ♦ ♣) and pips for 2–10; J/Q/K/A are our hero portraits (COURT_HERO) with the suit as an emblem;
 * a lattice back.
 * viewBox 100×140 — size it with CSS (width; height follows the 5:7 ratio).
 *
 * `card` = { rank: 'A'|'2'…'10'|'J'|'Q'|'K', value: number, suit: 'S'|'H'|'D'|'C', red: boolean } or null (→ back).
 * `corner`:
 *   'classic' (default, Durak) — small rank over suit in two corners;
 *   'large'   (Solitaire)      — big "rank suit" on one line in the top-left corner (readable when cards are fanned
 *                                and only the top strip is visible), centre art slightly scaled down to make room.
 */

export const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' };

/** Picture cards are our heroes: J Spider-Man, Q Wolverine, K Batman, A Captain. */
const COURT_HERO = {
  J: '/character/Spiderman_Head.png',
  Q: '/character/Wolverine_Head.png',
  K: '/character/Batman_Head.png',
  A: '/character/Captain_Head.png',
};

const INK = { red: '#d61f45', black: '#1b1530' };

const isCourtRank = (rank) => rank === 'J' || rank === 'Q' || rank === 'K' || rank === 'A';

// pip centres for 2..10 (bottom-half pips are drawn upside down, like real cards)
const PIPS = {
  2: [[50, 34], [50, 106]],
  3: [[50, 34], [50, 70], [50, 106]],
  4: [[32, 34], [68, 34], [32, 106], [68, 106]],
  5: [[32, 34], [68, 34], [50, 70], [32, 106], [68, 106]],
  6: [[32, 34], [68, 34], [32, 70], [68, 70], [32, 106], [68, 106]],
  7: [[32, 34], [68, 34], [50, 52], [32, 70], [68, 70], [32, 106], [68, 106]],
  8: [[32, 34], [68, 34], [50, 52], [32, 70], [68, 70], [50, 88], [32, 106], [68, 106]],
  9: [[32, 32], [68, 32], [32, 57], [68, 57], [50, 70], [32, 83], [68, 83], [32, 108], [68, 108]],
  10: [[32, 32], [68, 32], [50, 45], [32, 57], [68, 57], [32, 83], [68, 83], [50, 95], [32, 108], [68, 108]],
};

// lattice of small diamonds for the card back, built once
const BACK_LATTICE = (() => {
  let d = '';
  for (let y = 16; y <= 124; y += 12) {
    for (let x = 16 + ((y / 12) % 2 ? 6 : 0); x <= 84; x += 12) {
      d += `M${x} ${y - 4}L${x + 4} ${y}L${x} ${y + 4}L${x - 4} ${y}Z`;
    }
  }
  return d;
})();

const SERIF = "Georgia, 'Times New Roman', serif";

function Corner({ rank, sym, color }) {
  return (
    <g fill={color} fontFamily={SERIF} fontWeight="700" textAnchor="middle">
      <text x="12" y="22" fontSize={rank === '10' ? 17 : 20} letterSpacing={rank === '10' ? -1.5 : 0}>{rank}</text>
      <text x="12" y="40" fontSize="17">{sym}</text>
    </g>
  );
}

/** One-line "rank suit" index, ~0.34 card widths tall. */
function LargeCorner({ rank, sym, color }) {
  const ten = rank === '10';
  return (
    <text x="5" y="31" fill={color} fontFamily={SERIF} fontWeight="700" data-corner="large">
      <tspan fontSize={ten ? 31 : 35} letterSpacing={ten ? -3 : 0}>{rank}</tspan>
      <tspan fontSize="29" dx="1">{sym}</tspan>
    </text>
  );
}

function Headgear({ rank }) {
  if (rank === 'K') {
    return <path d="M36 40 L39 24 L45 33 L50 20 L55 33 L61 24 L64 40 Z" fill="#ffc531" stroke="#9a6a00" strokeWidth="1.2" />;
  }
  if (rank === 'Q') {
    return (
      <g>
        <path d="M38 38 Q50 24 62 38 Z" fill="#ffc531" stroke="#9a6a00" strokeWidth="1.2" />
        <circle cx="50" cy="30" r="2.4" fill="#fff" />
      </g>
    );
  }
  if (rank === 'A') {
    return <path d="M40 38 L50 22 L60 38 L50 33 Z" fill="#ffc531" stroke="#9a6a00" strokeWidth="1.2" />;
  }
  return null; // Jack — the hero as is
}

/** J / Q / K / A: a framed portrait of one of our heroes with headgear, the classic suit as the emblem. */
function CourtFigure({ rank, sym, color, red }) {
  return (
    <g>
      <rect x="21" y="20" width="58" height="100" rx="5" fill={red ? '#ffe7ec' : '#ebe7f8'} stroke={color} strokeWidth="1.6" />
      <path d="M26 118 Q50 86 74 118 Z" fill={color} opacity="0.85" />
      <image href={COURT_HERO[rank]} x="27" y="36" width="46" height="50" preserveAspectRatio="xMidYMid meet" data-hero={rank} />
      <Headgear rank={rank} />
      <circle cx="50" cy="102" r="11" fill="#fff" stroke={color} strokeWidth="1.4" />
      <text x="50" y="108" fontSize="17" textAnchor="middle" fill={color}>{sym}</text>
    </g>
  );
}

function CardFace({ card, corner }) {
  const color = card.red ? INK.red : INK.black;
  const sym = SUIT_SYMBOL[card.suit];
  const large = corner === 'large';
  let center;
  if (isCourtRank(card.rank)) {
    center = <CourtFigure rank={card.rank} sym={sym} color={color} red={card.red} />;
  } else {
    const value = Number(card.value) || Number(card.rank);
    center = (
      <g fill={color} fontSize="21" textAnchor="middle">
        {(PIPS[value] || []).map(([x, y]) => (
          <text key={`${x}-${y}`} x={x} y={y + 7} transform={y > 70 ? `rotate(180 ${x} ${y})` : undefined}>{sym}</text>
        ))}
      </g>
    );
  }
  return (
    <>
      <rect x="1" y="1" width="98" height="138" rx="9" fill="#fffdf8" stroke="#cfc8de" strokeWidth="1.5" />
      {large ? <LargeCorner rank={card.rank} sym={sym} color={color} /> : <Corner rank={card.rank} sym={sym} color={color} />}
      <g transform="rotate(180 50 70)"><Corner rank={card.rank} sym={sym} color={color} /></g>
      {/* the large index takes the top-left strip — shrink the centre art below it */}
      {large ? <g transform="translate(16 24) scale(0.68)">{center}</g> : center}
    </>
  );
}

function CardBack() {
  return (
    <>
      <rect x="1" y="1" width="98" height="138" rx="9" fill="#3a1d7c" stroke="#1d0f45" strokeWidth="1.5" />
      <rect x="7" y="7" width="86" height="126" rx="6" fill="#5b2fc4" stroke="#cfff1f" strokeOpacity="0.55" strokeWidth="1.4" />
      <path d={BACK_LATTICE} fill="#7d55e8" opacity="0.65" />
      <circle cx="50" cy="70" r="15" fill="#2a1460" stroke="#cfff1f" strokeWidth="1.6" />
      <text x="50" y="77" fontSize="20" fontWeight="900" textAnchor="middle" fill="#cfff1f" fontFamily="Montserrat, sans-serif">D</text>
    </>
  );
}

function PlayingCardArt({ card, className = '', style, corner = 'classic' }) {
  return (
    <svg className={className} viewBox="0 0 100 140" style={style} aria-hidden="true" focusable="false">
      {card ? <CardFace card={card} corner={corner} /> : <CardBack />}
    </svg>
  );
}

export default memo(PlayingCardArt);
